import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Archive, ArchiveRestore, Pencil, Plus, Search, Trash2 } from "lucide-react";
import type { Tables } from "@/integrations/supabase/types";
import { HourTotals, emptyTotals, fmtEur, fmtHours, loadOrderHourTotals } from "@/lib/lnwHours";
import { parseDecimal, toInputDecimal } from "@/lib/parseDecimal";
import { canCloseOrder, canReopen, dbErrorMessage } from "@/lib/closeRules";
import { ArchiveSection, ClosedBadge } from "@/components/admin/ArchiveSection";

type Contact = Tables<"customer_contacts">;
type Order = Tables<"orders">;
type ProjectRow = Tables<"projects"> & { customers: { company: string } | null };
type OrderRow = Order & { projects: (Tables<"projects"> & { customers: { company: string } | null }) | null };

const emptyForm = {
  project_id: "",
  contact_id: "none",
  name: "",
  order_number: "",
  hourly_rate: "",
  budget_amount: "",
  budget_hours: "",
  status: "active",
  notes: "",
};

const num = (v: string) => parseDecimal(v);

const statusLabel: Record<string, string> = {
  active: "Aktiv",
  closed: "Inaktiv",
};

const AdminOrders = () => {
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [timesheets, setTimesheets] = useState<{ order_id: string; closed_at: string | null }[]>([]);
  const [totals, setTotals] = useState<Record<string, HourTotals>>({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Order | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [remainingEdit, setRemainingEdit] = useState<{ id: string; value: string } | null>(null);
  const [savingRemaining, setSavingRemaining] = useState(false);
  const [search, setSearch] = useState("");
  const [archiveOpen, setArchiveOpen] = useState(false);

  const load = async () => {
    const [{ data: o }, { data: p }, { data: c }, { data: ts }, hourTotals] = await Promise.all([
      supabase.from("orders").select("*, projects(*, customers(company))").order("created_at", { ascending: false }),
      supabase.from("projects").select("*, customers(company)").order("name"),
      supabase.from("customer_contacts").select("*").order("name"),
      supabase.from("timesheets").select("order_id, closed_at"),
      loadOrderHourTotals(),
    ]);
    setOrders((o ?? []) as unknown as OrderRow[]);
    setProjects((p ?? []) as unknown as ProjectRow[]);
    setContacts(c ?? []);
    setTimesheets(ts ?? []);
    setTotals(hourTotals);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const summary = useMemo(() => {
    let open = 0;
    let remaining = 0;
    let withBudget = 0;
    for (const o of orders) {
      const t = totals[o.id] ?? emptyTotals();
      open += t.open;
      if (o.budget_hours != null) {
        withBudget += 1;
        remaining += Number(o.budget_hours) - t.approved - Number(o.external_hours ?? 0);
      }
    }
    return { open, remaining, withBudget };
  }, [orders, totals]);

  const openProjects = projects.filter((p) => !p.closed_at || p.id === form.project_id);
  const selectedProject = projects.find((p) => p.id === form.project_id) ?? null;
  const contactsForForm = useMemo(
    () => contacts.filter((c) => c.customer_id === selectedProject?.customer_id),
    [contacts, selectedProject],
  );

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (o: OrderRow) => {
    setEditing(o);
    setForm({
      project_id: o.project_id,
      contact_id: o.contact_id ?? "none",
      name: o.name,
      order_number: o.order_number ?? "",
      hourly_rate: toInputDecimal(o.hourly_rate),
      budget_amount: toInputDecimal(o.budget_amount),
      budget_hours: toInputDecimal(o.budget_hours),
      status: o.status,
      notes: o.notes ?? "",
    });
    setDialogOpen(true);
  };

  const onProjectChange = (projectId: string) => {
    const project = projects.find((p) => p.id === projectId);
    setForm((prev) => ({
      ...prev,
      project_id: projectId,
      contact_id: project?.contact_id ?? "none",
      hourly_rate: prev.hourly_rate || toInputDecimal(project?.hourly_rate),
    }));
  };

  const recalcHours = (amount: string, rate: string) => {
    const a = num(amount);
    const r = num(rate);
    if (a != null && r != null && r > 0) return toInputDecimal(Math.round((a / r) * 100) / 100);
    return "";
  };

  const save = async () => {
    if (!form.project_id || !form.name.trim()) {
      toast.error("Bitte wählen Sie ein Projekt und geben Sie eine Auftragsbezeichnung an.");
      return;
    }
    setSaving(true);
    const payload = {
      project_id: form.project_id,
      contact_id: form.contact_id === "none" ? null : form.contact_id,
      name: form.name.trim(),
      order_number: form.order_number.trim() || null,
      hourly_rate: num(form.hourly_rate),
      budget_amount: num(form.budget_amount),
      budget_hours: num(form.budget_hours),
      status: form.status,
      notes: form.notes.trim() || null,
    };
    const { error } = editing
      ? await supabase.from("orders").update(payload).eq("id", editing.id)
      : await supabase.from("orders").insert(payload);
    setSaving(false);
    if (error) {
      toast.error("Speichern fehlgeschlagen");
      return;
    }
    toast.success("Auftrag gespeichert");
    setDialogOpen(false);
    load();
  };

  const startRemainingEdit = (o: OrderRow, remaining: number | null) => {
    setRemainingEdit({ id: o.id, value: remaining != null ? String(remaining).replace(".", ",") : "" });
  };

  const saveRemaining = async (o: OrderRow) => {
    if (!remainingEdit) return;
    const value = num(remainingEdit.value);
    if (value == null) {
      toast.error("Bitte geben Sie die Reststunden als Zahl an.");
      return;
    }
    const approved = (totals[o.id] ?? emptyTotals()).approved;
    const budget = o.budget_hours != null ? Number(o.budget_hours) : null;
    if (budget == null) {
      toast.error("Bitte hinterlegen Sie zuerst ein Kontingent für diesen Auftrag.");
      return;
    }
    const external = Math.round((budget - approved - value) * 100) / 100;
    setSavingRemaining(true);
    const { error } = await supabase.from("orders").update({ external_hours: external }).eq("id", o.id);
    setSavingRemaining(false);
    if (error) {
      toast.error("Reststunden konnten nicht gespeichert werden");
      return;
    }
    toast.success("Reststunden aktualisiert");
    setRemainingEdit(null);
    load();
  };

  const remove = async (o: OrderRow) => {
    if (!window.confirm(`Auftrag „${o.name}" wirklich löschen? Zugehörige Leistungsnachweise werden ebenfalls gelöscht.`)) return;
    const { error } = await supabase.from("orders").delete().eq("id", o.id);
    if (error) toast.error(dbErrorMessage(error, "Löschen fehlgeschlagen"));
    else {
      toast.success("Auftrag gelöscht");
      load();
    }
  };

  const toggleClosed = async (o: OrderRow) => {
    const closing = !o.closed_at;
    if (closing && !window.confirm(`Auftrag „${o.name}" abschließen und archivieren?`)) return;
    const { error } = await supabase.from("orders").update({ closed_at: closing ? new Date().toISOString() : null }).eq("id", o.id);
    if (error) toast.error(dbErrorMessage(error, "Aktion fehlgeschlagen"));
    else {
      toast.success(closing ? "Auftrag abgeschlossen" : "Auftrag wieder geöffnet");
      load();
    }
  };

  const term = search.trim().toLowerCase();
  const matches = (o: OrderRow) =>
    !term ||
    [o.name, o.order_number ?? "", o.projects?.name ?? "", o.projects?.customers?.company ?? ""].join(" ").toLowerCase().includes(term);
  const activeOrders = orders.filter((o) => !o.closed_at && matches(o));
  const closedOrders = orders.filter((o) => !!o.closed_at && matches(o));
  const showArchive = archiveOpen || (!!term && closedOrders.length > 0);

  const header = (
    <TableHeader>
      <TableRow>
        <TableHead>Auftrag</TableHead>
        <TableHead className="hidden md:table-cell">Kunde / Projekt</TableHead>
        <TableHead className="hidden lg:table-cell">Ansprechpartner</TableHead>
        <TableHead className="text-right">Kontingent</TableHead>
        <TableHead className="text-right">Genehmigt</TableHead>
        <TableHead className="text-right">Offen</TableHead>
        <TableHead className="text-right">Rest</TableHead>
        <TableHead className="w-32 text-right">Aktionen</TableHead>
      </TableRow>
    </TableHeader>
  );

  const renderRow = (o: OrderRow) => {
    const t = totals[o.id] ?? emptyTotals();
    const budget = o.budget_hours != null ? Number(o.budget_hours) : null;
    const external = Number(o.external_hours ?? 0);
    const remaining = budget != null ? budget - t.approved - external : null;
    const contact = contacts.find((c) => c.id === o.contact_id);
    const check = canCloseOrder(timesheets.filter((ts) => ts.order_id === o.id));
    const closed = !!o.closed_at;
    return (
      <TableRow key={o.id} className={closed ? "text-muted-foreground" : undefined}>
        <TableCell>
          <div className="font-medium">{o.name}</div>
          <div className="text-xs text-muted-foreground">
            {o.order_number ?? "ohne Auftragskennung"}
            {o.hourly_rate != null ? ` · ${fmtEur(Number(o.hourly_rate))}/h` : ""}
          </div>
          {o.status !== "active" && <Badge variant="secondary" className="mt-1">{statusLabel[o.status] ?? o.status}</Badge>}
          {o.closed_at && <ClosedBadge closedAt={o.closed_at} />}
        </TableCell>
        <TableCell className="hidden md:table-cell">
          <div>{o.projects?.customers?.company ?? "–"}</div>
          <div className="text-xs text-muted-foreground">{o.projects?.name ?? "–"}</div>
        </TableCell>
        <TableCell className="hidden lg:table-cell">{contact?.name ?? "–"}</TableCell>
        <TableCell className="text-right">{budget != null ? `${fmtHours(budget)} h` : "–"}</TableCell>
        <TableCell className="text-right">{fmtHours(t.approved)} h</TableCell>
        <TableCell className="text-right text-muted-foreground">{fmtHours(t.open)} h</TableCell>
        <TableCell className={`text-right font-medium ${remaining != null && remaining < 0 ? "text-destructive" : ""}`}>
          {remainingEdit?.id === o.id ? (
            <div className="flex items-center justify-end gap-1">
              <Input
                autoFocus
                inputMode="decimal"
                className="h-8 w-24 text-right"
                value={remainingEdit.value}
                onChange={(e) => setRemainingEdit({ id: o.id, value: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveRemaining(o);
                  if (e.key === "Escape") setRemainingEdit(null);
                }}
              />
              <Button size="sm" onClick={() => saveRemaining(o)} disabled={savingRemaining}>OK</Button>
              <Button size="sm" variant="ghost" onClick={() => setRemainingEdit(null)}>Abbrechen</Button>
            </div>
          ) : closed ? (
            <span className="px-2">{remaining != null ? `${fmtHours(remaining)} h` : "–"}</span>
          ) : (
            <button
              type="button"
              className="rounded px-2 py-1 underline-offset-4 hover:underline"
              onClick={() => startRemainingEdit(o, remaining)}
              title="Reststunden bearbeiten (Kontingent bleibt unverändert)"
            >
              {remaining != null ? `${fmtHours(remaining)} h` : "–"}
            </button>
          )}
        </TableCell>
        <TableCell className="text-right whitespace-nowrap">
          {closed ? (
            (() => {
              const r = canReopen(o.projects?.closed_at, "Projekt");
              return (
                <span title={r.ok ? "Wieder öffnen" : r.reason}>
                  <Button variant="ghost" size="icon" onClick={() => toggleClosed(o)} disabled={!r.ok} aria-label="Wieder öffnen">
                    <ArchiveRestore className="h-4 w-4" />
                  </Button>
                </span>
              );
            })()
          ) : (
            <>
              <span title={check.ok ? "Abschließen" : check.reason}>
                <Button variant="ghost" size="icon" onClick={() => toggleClosed(o)} disabled={!check.ok} aria-label="Abschließen">
                  <Archive className="h-4 w-4" />
                </Button>
              </span>
              <Button variant="ghost" size="icon" onClick={() => openEdit(o)} aria-label="Bearbeiten">
                <Pencil className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => remove(o)} aria-label="Löschen">
                <Trash2 className="h-4 w-4" />
              </Button>
            </>
          )}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <Helmet>
        <title>Aufträge | Leistungsnachweise</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Aufträge</h1>
        <Button className="gap-2" onClick={openNew} disabled={openProjects.length === 0}>
          <Plus className="h-4 w-4" /> Neuer Auftrag
        </Button>
      </div>

      {projects.length === 0 && !loading && (
        <p className="mb-4 text-sm text-muted-foreground">Legen Sie zuerst ein Projekt an, bevor Sie Aufträge erstellen.</p>
      )}

      {!loading && (orders.length > 0 || summary.open > 0) && (
        <div className="mb-6 grid gap-4 sm:grid-cols-2">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Offene Stunden</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-semibold">{fmtHours(summary.open)} h</p>
              <p className="text-xs text-muted-foreground">Summe aller nicht genehmigten Stunden über alle Aufträge</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Reststunden</CardTitle>
            </CardHeader>
            <CardContent>
              <p className={`text-3xl font-semibold ${summary.remaining < 0 ? "text-destructive" : ""}`}>{fmtHours(summary.remaining)} h</p>
              <p className="text-xs text-muted-foreground">
                Kontingent minus genehmigte Stunden{summary.withBudget > 0 ? ` · ${summary.withBudget} ${summary.withBudget === 1 ? "Auftrag" : "Aufträge"} mit Kontingent` : ""}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="relative mb-4 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Suche nach Auftrag, Kennung, Projekt oder Kunde" className="pl-9" aria-label="Aufträge durchsuchen" />
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            {header}
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="py-8 text-center text-muted-foreground">Laden…</TableCell></TableRow>
              ) : activeOrders.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="py-8 text-center text-muted-foreground">{term ? "Keine aktiven Treffer." : "Keine aktiven Aufträge."}</TableCell></TableRow>
              ) : (
                activeOrders.map(renderRow)
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {!loading && closedOrders.length > 0 && (
        <ArchiveSection count={closedOrders.length} open={showArchive} onOpenChange={setArchiveOpen}>
          <Table>
            {header}
            <TableBody>{closedOrders.map(renderRow)}</TableBody>
          </Table>
        </ArchiveSection>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Auftrag bearbeiten" : "Neuer Auftrag"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Projekt *</Label>
              <Select value={form.project_id} onValueChange={onProjectChange}>
                <SelectTrigger><SelectValue placeholder="Projekt wählen" /></SelectTrigger>
                <SelectContent>
                  {openProjects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.customers?.company ? `${p.customers.company} · ` : ""}{p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="o-name">Auftragsbezeichnung *</Label>
              <Input id="o-name" placeholder="z. B. OT-Assessment Phase 2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="o-number">Auftragskennung (erscheint im Leistungsnachweis)</Label>
              <Input id="o-number" value={form.order_number} onChange={(e) => setForm({ ...form, order_number: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Ansprechpartner des Kunden</Label>
              <Select value={form.contact_id} onValueChange={(v) => setForm({ ...form, contact_id: v })} disabled={!form.project_id}>
                <SelectTrigger><SelectValue placeholder="Ansprechpartner wählen" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Kein Ansprechpartner</SelectItem>
                  {contactsForForm.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}{c.email ? ` · ${c.email}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Vorbelegt mit dem Ansprechpartner des Projekts, je Auftrag änderbar.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="o-rate">Stundensatz (€ netto)</Label>
                <Input
                  id="o-rate"
                  inputMode="decimal"
                  value={form.hourly_rate}
                  onChange={(e) => {
                    const hourly_rate = e.target.value;
                    setForm((prev) => ({ ...prev, hourly_rate, budget_hours: recalcHours(prev.budget_amount, hourly_rate) || prev.budget_hours }));
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="o-amount">Auftragssumme (€ netto)</Label>
                <Input
                  id="o-amount"
                  inputMode="decimal"
                  value={form.budget_amount}
                  onChange={(e) => {
                    const budget_amount = e.target.value;
                    setForm((prev) => ({ ...prev, budget_amount, budget_hours: recalcHours(budget_amount, prev.hourly_rate) || prev.budget_hours }));
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="o-hours">Stundenkontingent</Label>
                <Input id="o-hours" inputMode="decimal" value={form.budget_hours} onChange={(e) => setForm({ ...form, budget_hours: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Aktiv</SelectItem>
                  <SelectItem value="closed">Inaktiv</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="o-notes">Notiz</Label>
              <Textarea id="o-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={save} disabled={saving}>{saving ? "Speichern…" : "Speichern"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminOrders;
